import { Autocomplete, Box, FormControl, Grid, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { EditProfileFormSchema } from '../../../../pages/EditProfile';
import { formatPhoneNumber } from 'utility/helpers';
import { SyntheticEvent, useMemo } from 'react';
import { useConfigQuery } from 'hooks/useCompanyQueries';
import useUserData from 'hooks/useUserData';

type OptionsResult = {
  jobTitlesOptions: string[];
  marketSectorsOptions: string[];
  regionsOptions: string[];
  departmentsOptions: string[];
  subDepartmentsOptions: string[];
};

function EditProfileFormFields() {
  const { companyId, user } = useUserData();
  const { entries: jobTitles } = useConfigQuery(companyId, 'JOB_TITLES');
  const { entries: marketSectors } = useConfigQuery(companyId, 'MARKET_SECTORS');
  const { entries: regions } = useConfigQuery(companyId, 'REGIONS');
  const { entries: departments } = useConfigQuery(companyId, 'DEPARTMENTS');
  const { entries: subDepartments } = useConfigQuery(companyId, 'SUB_DEPARTMENTS');

  const {
    jobTitlesOptions,
    marketSectorsOptions,
    regionsOptions,
    departmentsOptions,
    subDepartmentsOptions,
  }: OptionsResult = useMemo(() => {
    const list = {
      jobTitles,
      marketSectors,
      regions,
      departments,
      subDepartments,
    };
    const result: OptionsResult = {
      jobTitlesOptions: [],
      marketSectorsOptions: [],
      regionsOptions: [],
      departmentsOptions: [],
      subDepartmentsOptions: [],
    };
    Object.keys(list).forEach((key) => {
      result[`${key}Options`] = list[key] ? list[key].map((p) => p.entryCode) : [];
    });
    return result;
  }, [jobTitles, marketSectors, regions, departments, subDepartments]);

  const {
    control,
    formState: { isSubmitting },
    getValues,
    setValue,
  } = useFormContext<EditProfileFormSchema>();
  return (
    <Container>
      <Title>Personal Information</Title>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <TextField
                  {...field}
                  value={!!field.value.trim() ? field.value : ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || ''}
                  fullWidth
                  label={'First Name'}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            )}
            name="firstname"
          />
        </Grid>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <TextField
                  {...field}
                  value={!!field.value.trim() ? field.value : ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || ''}
                  fullWidth
                  label={'Last Name'}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            )}
            name="lastname"
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={12}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <TextField
                  {...field}
                  value={!!field.value.trim() ? field.value : ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || ''}
                  fullWidth
                  label="Email"
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  tabIndex={-1}
                  disabled
                />
              </FormControl>
            )}
            name="email"
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={12}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <TextField
                  {...field}
                  value={!!field.value.trim() ? field.value : ''}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || ''}
                  fullWidth
                  label="Phone Number (optional)"
                  variant="outlined"
                  onChange={(e) => {
                    field.onChange(formatPhoneNumber(e.target.value));
                  }}
                />
              </FormControl>
            )}
            name="phoneNumber"
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={12}>
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <Autocomplete
                  {...field}
                  freeSolo
                  autoHighlight
                  value={field.value || null}
                  onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                    setValue('title', value || '', { shouldDirty: true, shouldValidate: true })
                  }
                  onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                    setValue('title', value || '', { shouldDirty: true, shouldValidate: true })
                  }
                  options={jobTitlesOptions}
                  noOptionsText="No Job Titles Found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      placeholder="Job Title"
                      label="Job Title"
                    />
                  )}
                />
              </FormControl>
            )}
            name="title"
            defaultValue=""
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={12}>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <FormControl sx={{ my: 1 }} fullWidth>
                <TextField
                  {...field}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  variant="outlined"
                  label="Company Name"
                  placeholder="Company Name"
                  InputLabelProps={{ shrink: true }}
                  disabled={!!(user?.primaryCompanyId && user?.primaryCompanyName)}
                />
              </FormControl>
            )}
            name="primaryCompanyName"
            defaultValue=""
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <Autocomplete
                  {...field}
                  freeSolo
                  autoHighlight
                  value={field.value || null}
                  onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                    setValue('marketSector', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                    setValue('marketSector', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  options={marketSectorsOptions}
                  noOptionsText="No Market Sectors Found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      label="Market Sector"
                    />
                  )}
                />
              </FormControl>
            )}
            name="marketSector"
            defaultValue=""
          />
        </Grid>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <Autocomplete
                  {...field}
                  freeSolo
                  autoHighlight
                  value={field.value || null}
                  onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                    setValue('region', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                    setValue('region', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  options={regionsOptions}
                  noOptionsText="No Regions Found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      label="Region"
                    />
                  )}
                />
              </FormControl>
            )}
            name="region"
            defaultValue=""
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <Autocomplete
                  {...field}
                  freeSolo
                  autoHighlight
                  value={field.value || null}
                  onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                    setValue('department', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                    setValue('department', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  options={departmentsOptions}
                  noOptionsText="No Department Found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      label="Department"
                    />
                  )}
                />
              </FormControl>
            )}
            name="department"
            defaultValue=""
          />
        </Grid>
        <Grid item sm={12} lg={6}>
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <FormControl fullWidth sx={{ my: 1 }}>
                <Autocomplete
                  {...field}
                  freeSolo
                  autoHighlight
                  value={field.value || null}
                  onChange={(_: SyntheticEvent<Element>, value: string | null) =>
                    setValue('subDepartment', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onInputChange={(_: SyntheticEvent<Element>, value: string) =>
                    setValue('subDepartment', value || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  options={subDepartmentsOptions}
                  noOptionsText="No Sub-Department Found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!fieldState.error}
                      helperText={fieldState?.error?.message || ''}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      label="Sub-Department"
                    />
                  )}
                />
              </FormControl>
            )}
            name="subDepartment"
            defaultValue=""
          />
        </Grid>
      </Grid>
    </Container>
  );
}

const Container = styled(Box, { label: 'Container' })(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.custom.white,
  borderLeft: `4px solid transparent`,
  borderRadius: 8,
  padding: 16,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  margin: '8px 0 12px',
  '&:hover': {
    '& .reorder-icon': {
      visibility: 'visible',
    },
  },
}));

export const Title = styled(Box, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
  textTransform: 'capitalize',
}));

export default EditProfileFormFields;
